import {
  createContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { PackageInformationRequest, PackageSessionResponse } from '~/types/api';
import { useOptions } from '~/hooks/useOptions';
import { InferredBookingFlowOptions } from '~/types/options';
import useSessionId from '~/hooks/bookingFlow/useSessionId';
import { initialFormData, useInformationForm } from '~/hooks/bookingFlow/useInformationForm';
import usePackageSession from '~/hooks/bookingFlow/usePackageSession';

type BookingFlowInformationContextType = {
  informationFormValue?: PackageInformationRequest;
  packageSessionData?: PackageSessionResponse;
  packageSessionError: Error | null;
  packageSessionPending: boolean;
};

export const BookingFlowInformationContext = createContext<BookingFlowInformationContextType>({
  informationFormValue: undefined,
  packageSessionData: undefined,
  packageSessionError: null,
  packageSessionPending: false,
});

type Props = {
  children: React.ReactNode;
};

export default function BookingFlowInformationProvider({
  children,
}: Props) {
  // Retrieve the options passed to the embed, for example the locale, package id, etc.
  const options = useOptions() as InferredBookingFlowOptions;

  // Retrieve the session ID from the URL, if it is set.
  const { sessionId } = useSessionId();

  if (!sessionId) {
    throw new Error('Session ID is not set');
  }

  // Retrieve the package session from the backend
  const {
    data: packageSessionData,
    error: packageSessionError,
    isPending: packageSessionPending,
  } = usePackageSession({
    sessionId,
    locale: options.locale,
  });

  // Retrieve information form data from the backend, this can be undefined if there's
  // no information in the session yet.
  const {
    data: informationFormData,
  } = useInformationForm(sessionId);

  const [
    informationFormValue,
    setInformationFormValue,
  ] = useState<PackageInformationRequest>(
    initialFormData(sessionId || '', options.pax, options.locale),
  );

  useEffect(() => {
    if (informationFormData) {
      setInformationFormValue(informationFormData);
    }
  }, [informationFormData]);

  useEffect(() => {
    if (sessionId && informationFormValue.session_id !== sessionId) {
      setInformationFormValue((prev) => {
        return { ...prev, session_id: sessionId };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const value = useMemo(() => {
    return {
      informationFormValue,
      packageSessionData,
      packageSessionError,
      packageSessionPending,
    };
  }, [
    informationFormValue,
    packageSessionData,
    packageSessionError,
    packageSessionPending,
  ]);

  return (
    <BookingFlowInformationContext.Provider value={value}>
      {children}
    </BookingFlowInformationContext.Provider>
  );
}
